import React, { useEffect, forwardRef, useImperativeHandle } from "react";
import { AppStreamState, AppStreamConnectionState } from "common/components/appstream";
import { Table, Button, Modal, Card, Spinner } from "react-bootstrap";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { FormattedDate, FormattedTime } from "react-intl";
import { useSuperState } from "helpers/hooks";
import { useApp } from "common/context";
import moment from "moment";

const MetricsChart = ({ metrics }) => {

    if (!metrics?.length)
        return (
            <div className="text-center text-muted py-3">
                No metrics data available
            </div>
        );

    const data = metrics.map(m => ({

        time: moment(m.created_at).format('HH:mm'),
        CPU: Math.round(m.cpu_usage),
        Memory: Math.round(m.memory_usage)

    }));

    return (
        <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid vertical={false} stroke="#d1d1d1"/>
                    <XAxis dataKey="time" fontSize={12}/>
                    <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} fontSize={12}/>
                    <Tooltip formatter={v => `${v}%`}/>
                    <Legend/>
                    <Line type="monotone" dataKey="CPU" stroke="#0d6efd" strokeWidth={2} dot={false}/>
                    <Line type="monotone" dataKey="Memory" stroke="#6f42c1" strokeWidth={2} dot={false}/>
                </LineChart>
            </ResponsiveContainer>
        </div>
    );

};

const ConnectionChart = ({ metrics }) => {

    if (!metrics?.length)
        return (
            <div className="text-center text-muted py-3">
                No connection data available
            </div>
        );

    const data = metrics.map(m => ({

        time: moment(m.created_at).format('HH:mm'),
        'RTT (ms)': m.rtt_ms != null ? Math.round(m.rtt_ms) : null,
        'Jitter (ms)': m.jitter_ms != null ? Math.round(m.jitter_ms) : null,
        'Packet Loss (%)': m.packet_loss_pct != null ? Math.round(m.packet_loss_pct * 10) / 10 : null

    }));

    return (
        <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid vertical={false} stroke="#d1d1d1"/>
                    <XAxis dataKey="time" fontSize={12}/>
                    <YAxis yAxisId="ms" orientation="left" tickFormatter={v => `${v}ms`} fontSize={12}/>
                    <YAxis yAxisId="pct" orientation="right" domain={[0, 'auto']} tickFormatter={v => `${v}%`} fontSize={12}/>
                    <Tooltip/>
                    <Legend/>
                    <Line yAxisId="ms" type="monotone" dataKey="RTT (ms)" stroke="#dc3545" strokeWidth={2} dot={false}/>
                    <Line yAxisId="ms" type="monotone" dataKey="Jitter (ms)" stroke="#6f42c1" strokeWidth={2} dot={false}/>
                    <Line yAxisId="pct" type="monotone" dataKey="Packet Loss (%)" stroke="#fd7e14" strokeWidth={2} dot={false}/>
                </LineChart>
            </ResponsiveContainer>
        </div>
    );

};

const SessionDetailModal = forwardRef((props, ref) => {

    const [state, setState, resetState] = useSuperState({
        visible: false,
        loading: false,
        session: null,
        records: [],
        metrics: [],
        connection_metrics: []
    });

    const { api } = useApp();

    const show = async session => {

        setState({
            visible: true,
            loading: true,
            session
        });

        const session_guid = session?.session_guid;
        const appstream_instance_id = session?.appstream_instance_id;
        const started_at = session?.started_at;
        const last_active = session?.last_active;

        let records = [];
        let metrics = [];
        let connection_metrics = [];

        // Fetch activity log records
        if (session_guid) {

            try {

                const result = await api.get(`/api/appstream/sessions/history/details/${session_guid}`);
                records = result?.records || [];

            } catch (e) {

                console.error('Error loading session history:', e);

            }

        }

        // Fetch instance metrics history
        if (appstream_instance_id) {

            try {

                const params = {};
                if (started_at) params.start_date = moment(started_at).toISOString();
                if (last_active) params.end_date = moment(last_active).toISOString();

                const result = await api.get(`/api/appstream/instances/metrics/history/${appstream_instance_id}`, params);
                metrics = result?.metrics || [];

            } catch (e) {

                console.error('Error loading metrics history:', e);

            }

        }

        // Fetch connection quality history
        if (session_guid) {

            try {

                const result = await api.get(`/api/appstream/sessions/connection-metrics/history/${session_guid}`);
                connection_metrics = result?.metrics || [];

            } catch (e) {

                console.error('Error loading connection metrics:', e);

            }

        }

        setState({ records, metrics, connection_metrics, loading: false });

    };

    const hide = () => resetState();

    useImperativeHandle(ref, () => ({
        show,
        hide
    }));

    useEffect(() => resetState, []);

    const { visible, loading, session, records, metrics, connection_metrics } = state;

    const {
        appstream_state, appstream_connection_state, appstream_user_id,
        appstream_instance_id, sms_user_name, sms_user_token,
        sms_practice_test_result_id, sms_uid, sms_ctag,
        sms_application_name
    } = session || {};

    return (
        <Modal show={visible} onHide={hide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    Session Details
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>

                <Card className="mb-3">
                    <Card.Header>General Details</Card.Header>
                    <Table bordered className="m-0">
                        <tbody>
                            <tr>
                                <th>Session State</th>
                                <td>
                                    <AppStreamState appstream_state={appstream_state}/>
                                </td>
                            </tr>
                            <tr>
                                <th>Connection State</th>
                                <td>
                                    <AppStreamConnectionState appstream_connection_state={appstream_connection_state}/>
                                </td>
                            </tr>
                            <tr>
                                <th>AppStream User</th>
                                <td><small>{appstream_user_id || '-'}</small></td>
                            </tr>
                            <tr>
                                <th>AppStream Instance ID</th>
                                <td>
                                    <small style={{ whiteSpace: 'nowrap' }}>
                                        {appstream_instance_id || '-'}
                                    </small>
                                </td>
                            </tr>
                            <tr>
                                <th>Application User</th>
                                <td><small>{sms_user_name || '-'}</small></td>
                            </tr>
                            <tr>
                                <th>Application UID</th>
                                <td><small>{sms_uid || '-'}</small></td>
                            </tr>
                            <tr>
                                <th>Application Product</th>
                                <td><small>{sms_application_name || '-'}</small></td>
                            </tr>
                            <tr>
                                <th>Application User Token</th>
                                <td><small>{sms_user_token || '-'}</small></td>
                            </tr>
                            <tr>
                                <th>Application Practice Test Result ID</th>
                                <td><small>{sms_practice_test_result_id || '-'}</small></td>
                            </tr>
                            <tr>
                                <th>Application Culture Tag</th>
                                <td><small>{sms_ctag || '-'}</small></td>
                            </tr>
                        </tbody>
                    </Table>
                </Card>

                <Card className="mb-3">
                    <Card.Header>Instance Metrics History</Card.Header>
                    <Card.Body>
                        {loading ?
                            (
                                <div className="text-center">
                                    <Spinner animation="border" size="sm"/>
                                </div>
                            )
                            :
                            (
                                <MetricsChart metrics={metrics}/>
                            )
                        }
                    </Card.Body>
                </Card>

                <Card className="mb-3">
                    <Card.Header>Connection Quality</Card.Header>
                    <Card.Body>
                        {loading ?
                            (
                                <div className="text-center">
                                    <Spinner animation="border" size="sm"/>
                                </div>
                            )
                            :
                            (
                                <ConnectionChart metrics={connection_metrics}/>
                            )
                        }
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header>Activity Log</Card.Header>
                    {loading ?
                        (
                            <Card.Body>
                                <div className="text-center">
                                    <Spinner animation="border" size="sm"/>
                                </div>
                            </Card.Body>
                        )
                        :
                        (
                            <Table bordered className="m-0">
                                <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>State</th>
                                        <th>Connection State</th>
                                        <th>Instance ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!!records?.length && records.map((record, k) => {

                                        const { appstream_state, appstream_connection_state, appstream_instance_id, created_at } = record;

                                        return (
                                            <tr key={k}>
                                                <td>
                                                    <FormattedDate value={created_at}/>
                                                    &nbsp;
                                                    <FormattedTime value={created_at}/>
                                                </td>
                                                <td>
                                                    <AppStreamState appstream_state={appstream_state}/>
                                                </td>
                                                <td>
                                                    <AppStreamConnectionState appstream_connection_state={appstream_connection_state}/>
                                                </td>
                                                <td>
                                                    <small style={{ whiteSpace: 'nowrap' }}>
                                                        {appstream_instance_id || '-'}
                                                    </small>
                                                </td>
                                            </tr>
                                        );

                                    })}
                                    {!records?.length &&
                                        <tr>
                                            <td colSpan={4} className="text-center">
                                                No activity records found
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </Table>
                        )
                    }
                </Card>

            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={hide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );

});

SessionDetailModal.displayName = 'SessionDetailModal';

export default SessionDetailModal;
